import { useState } from "react";
import { Building2, RefreshCw } from "lucide-react";
import { serviceNames, type ServiceState } from "./console-model";
import "./console-identity.css";

export function ServiceStatus({ state }: { state: ServiceState }) {
  return (
    <span className={`lwi-service-status is-${state}`}>
      <span aria-hidden="true" />
      {serviceNames[state]}
    </span>
  );
}

/** Shared organization photo; missing or failed images use the same building icon. */
export function OrganizationAvatar({
  name,
  imageUrl,
  rail = false,
}: {
  name: string;
  imageUrl?: string;
  rail?: boolean;
}) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const showImage = !!imageUrl && imageUrl !== failedUrl;
  return (
    <span
      className={"lwi-avatar" + (rail ? " lwi-avatar--rail" : "")}
      role={rail ? "img" : undefined}
      aria-label={rail ? name + " 프로필" : undefined}
      title={rail ? name : undefined}
      aria-hidden={rail ? undefined : true}
    >
      <span className="avatar avatar--lg tone-neutral">
        {showImage ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            key={imageUrl}
            src={imageUrl}
            alt=""
            width={46}
            height={46}
            onError={() => setFailedUrl(imageUrl ?? null)}
          />
        ) : (
          <Building2 size={22} aria-hidden="true" />
        )}
      </span>
    </span>
  );
}

/** Shared tenant identity; photo can be supplied when organization media is connected. */
export function OrganizationIdentity({
  name,
  imageUrl,
  state,
  subtitle,
  compact = false,
}: {
  name: string;
  initials?: string;
  imageUrl?: string;
  state: ServiceState;
  subtitle?: string;
  compact?: boolean;
}) {
  return (
    <div className={`lwi-identity${compact ? " is-compact" : ""}`}>
      <OrganizationAvatar name={name} imageUrl={imageUrl} />
      <div className="lwi-identity-copy">
        <div className="lwi-identity-heading">
          <strong title={name}>{name}</strong>
          <ServiceStatus state={state} />
        </div>
        {subtitle && <p>{subtitle}</p>}
      </div>
    </div>
  );
}

/** Snapshot freshness belongs beside the data, not in global navigation. */
export function DataFreshness({ polling = false }: { polling?: boolean }) {
  return (
    <div
      className={`lwi-freshness${polling ? " is-polling" : ""}`}
      role="status"
    >
      <span className="lwi-freshness-mode">
        <RefreshCw size={13} aria-hidden="true" />
        {polling ? "주기 갱신 중" : "실시간 반영"}
      </span>
      <span className="lwi-freshness-time">
        마지막 갱신 <time>14:12:40</time>
      </span>
    </div>
  );
}
